import { RecalculatedProductVariant, Recipe, Pension, Cycle } from '../../../core/types';

export function recipesMatch(a: Recipe[], b: Recipe[]): boolean {
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.length === sortedB.length && sortedA.every((r, i) => r === sortedB[i]);
}

export function getRecipeKey(recipes: Recipe[]): string {
  return [...recipes].sort().join(',');
}

export function findMatchingVariant(
  variants: RecalculatedProductVariant[],
  options: {
    recipes: Recipe[];
    pension?: Pension;
    cycle?: Cycle;
  },
): RecalculatedProductVariant | null {
  if (!variants || variants.length === 0) return null;

  return (
    variants.find((variant) => {
      if (!recipesMatch(variant.selectedOptions.recipes, options.recipes)) return false;
      if (options.pension !== undefined && variant.selectedOptions.pension !== options.pension)
        return false;
      if (options.cycle !== undefined && variant.selectedOptions.cycle !== options.cycle)
        return false;
      return true;
    }) || null
  );
}

export function hasMatchingVariant(
  variants: RecalculatedProductVariant[],
  options: {
    recipes: Recipe[];
    pension?: Pension;
    cycle?: Cycle;
  },
): boolean {
  return findMatchingVariant(variants, options) !== null;
}

export function getAvailableRecipeCombinations(variants: RecalculatedProductVariant[]): Recipe[][] {
  const combinations = new Set<string>();

  variants.forEach((variant) => {
    const key = getRecipeKey(variant.selectedOptions.recipes);
    combinations.add(key);
  });

  return Array.from(combinations).map((key) =>
    key
      .split(',')
      .filter(Boolean)
      .map((r) => r as Recipe),
  );
}

export function isRecipeCombinationAvailable(
  variants: RecalculatedProductVariant[],
  recipes: Recipe[],
  pension: Pension,
  cycle: Cycle,
): boolean {
  return findMatchingVariant(variants, { recipes, pension, cycle }) !== null;
}

export function getDefaultRecipes(
  variants: RecalculatedProductVariant[],
  disabledRecipes: string[],
  preferredRecipes?: Recipe[],
): Recipe[] {
  if (preferredRecipes && preferredRecipes.length > 0) {
    const validPreferred = preferredRecipes.filter((recipe) => !disabledRecipes.includes(recipe));
    if (validPreferred.length > 0) {
      const hasVariant = variants.some((variant) =>
        recipesMatch(variant.selectedOptions.recipes, validPreferred),
      );
      if (hasVariant) return validPreferred;
    }
  }

  for (const variant of variants) {
    const hasDisabled = variant.selectedOptions.recipes.some((recipe) =>
      disabledRecipes.includes(recipe),
    );
    if (!hasDisabled) {
      return variant.selectedOptions.recipes;
    }
  }

  return variants[0]?.selectedOptions.recipes || ['CG'];
}

// formatPrice used to live here as `price.toFixed(2).replace('.', ',')`. It moved to
// shared/helpers/formatMoney.ts as `formatAmount(value, locale)`, because the decimal
// separator has to follow the active locale and a util module cannot reach it.

// The dashboard gates on its own flag (custom.available_in_cd) so the
// storefront's custom.available_for_sale can differ. During rollout the CD flag
// may be missing on some variants, so fall back to the storefront flag when the
// CD one is absent. Both arrive as strings ("true"/"false").
export const resolveAvailableForSale = (variant: any): string | undefined => {
  const cd = variant?.metafields?.available_in_cd;
  const cdStr = cd == null ? '' : String(cd).trim();
  if (cdStr.length > 0) return cdStr;
  return variant?.metafields?.available_for_sale;
};

// A missing flag is treated as available (conservative): only an explicit
// "false" marks a variant as not-for-sale.
export const isVariantExplicitlyNotForSale = (variant: any): boolean =>
  String(resolveAvailableForSale(variant)).trim().toLowerCase() === 'false';
